import { Injectable } from '@angular/core';
import {
  CleanFillUserTotalType,
  CompanyQPTotalType,
  CompanyQPViewModelCompanyQPTotalTypePagedResultResponse,
  EditUserPoliciesAndRolesCommand,
  EditUserProfileCommand,
  IFillSiteUserSearchViewModelTotalTypePagedResultResponse,
  InviteLegalEntityCommand,
  InviteQualifiedPersonCommand,
  InviteQualifiedPersonListCommand,
  InviteUserCommand,
  IQualifiedPersonViewModel,
  ISourceSiteQPSearchViewModelTotalTypePagedResultResponse,
  ISourceSiteUserSearchViewModelTotalTypePagedResultResponse,
  IUserViewModel,
  QualifiedPersonSearchViewModelTotalTypePagedResultResponse,
  TotalType,
  UserSearchViewModelCleanFillUserTotalTypePagedResultResponse
} from '@services/cleanfill-api/cleanfill-api.generated';
import { HttpUsersService } from '@services/cleanfill-core/http-users/http-users.service';
import { AuthService } from '@services/core/auth.service';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StoreUsersService {
  // private activeUserSubj$ = new BehaviorSubject<ICurrentUserViewModel>(null);
  // activeUser$ = this.activeUserSubj$.asObservable();
  // activeUserId$ = this.activeUser$.pipe(map(user => user?.uniqueId || undefined));
  // activeUserPermissions$: Observable<string[] | Permission[]> = this.activeUser$.pipe(map(user => user?.policies));
  // activeUserRoles$: Observable<string[]> = this.activeUser$.pipe(map(user => user?.roles || []));
  // activeUserLegalEntityType$ = this.activeUser$.pipe(map(user => user?.legalEntityType));
  // activeUserLegalEntityUniqueId$ = this.activeUser$.pipe(map(user => user?.legalEntityUniqueId));

  private userSubj$ = new BehaviorSubject<IUserViewModel | IQualifiedPersonViewModel>(null);
  user$ = this.userSubj$.asObservable();

  private userRecordsSubj$ = new BehaviorSubject<UserSearchViewModelCleanFillUserTotalTypePagedResultResponse>(null);
  users$ = this.userRecordsSubj$.asObservable().pipe(map(payload => payload?.records || []));
  totalUsers$ = this.userRecordsSubj$
    .asObservable()
    .pipe(map(payload => payload?.totals.find(total => total.type === CleanFillUserTotalType.AllStatus)?.value || 500));

  private qpRecordsSubj$ = new BehaviorSubject<CompanyQPViewModelCompanyQPTotalTypePagedResultResponse>(null);
  qps$ = this.qpRecordsSubj$.asObservable().pipe(map(result => this.mapQpToTableData(result?.records) || []));
  totalQps$ = this.qpRecordsSubj$.pipe(map(v => v?.totals?.find(total => total.type === CompanyQPTotalType.AllStatus)?.value || 500));

  private allQPRecordsSubj$ = new BehaviorSubject<QualifiedPersonSearchViewModelTotalTypePagedResultResponse>(null);
  allQPs$ = this.allQPRecordsSubj$.asObservable().pipe(map(payload => payload?.records || []));
  totalAllQps$ = this.allQPRecordsSubj$.pipe(map(payload => payload?.totals.find(total => total.type === TotalType.Total)?.value || 500));

  // tslint:disable-next-line: max-line-length
  private userSourceSitesSub$ = new BehaviorSubject<
    ISourceSiteUserSearchViewModelTotalTypePagedResultResponse | ISourceSiteQPSearchViewModelTotalTypePagedResultResponse
  >(null);
  userSourceSites$ = this.userSourceSitesSub$.asObservable().pipe(map(payload => payload?.records || []));
  totalUserSourceSites$ = this.userSourceSitesSub$
    .asObservable()
    .pipe(map(payload => payload?.totals.find(total => total.type === TotalType.Total)?.value || 0));

  private userFillSiteSub$ = new BehaviorSubject<IFillSiteUserSearchViewModelTotalTypePagedResultResponse>(null);
  userFillSites$ = this.userFillSiteSub$.asObservable().pipe(map(payload => payload?.records || []));
  totalUserFillSites$ = this.userFillSiteSub$
    .asObservable()
    .pipe(map(payload => payload?.totals.find(total => total.type === TotalType.Total)?.value || 0));

  // public get activeUser() {
  //   return this.activeUserSubj$.value;
  // }
  public get users() {
    return this.userRecordsSubj$.value;
  }

  subscriptions = new Subscription();
  constructor(private userHttp: HttpUsersService, private authService: AuthService) { }

  // loadActiveUser() {
  //   const sub = this.userHttp.getActiveUser().subscribe((user: ICurrentUserViewModel) => this.activeUserSubj$.next(user));
  //   this.subscriptions.add(sub);
  // }
  get companyUniqueId$(): Observable<string> {
    return this.authService.activeUserLegalEntityUniqueId$;
  }
  private mapQpToTableData(items) {
    if (!items) {
      return items;
    }
    return items.map(item => {
      return { ...item, emailAddress: item.qpEmail, fullName: item.qpName, sitesAssigned: item.assignedSites, roles: item.permission };
    });
  }
  loadUsers(query: ISearchQuery): void {
    const sub = this.userHttp.getUsers(query).subscribe(users => this.userRecordsSubj$.next(users));
    this.subscriptions.add(sub);
  }
  loadAllUsers(query: ISearchQuery): void {
    const sub = this.userHttp.getUsers(query).subscribe(users => this.userRecordsSubj$.next(users));
    this.subscriptions.add(sub);
  }
  async loadQPUsers(query: ISearchQuery, companyUniqueId?: string): Promise<void> {
    const companyId = companyUniqueId || ((await this.companyUniqueId$.pipe(filter(Boolean), take(1)).toPromise()) as string);
    const sub = this.userHttp.getQPUsers(companyId, query).subscribe(users => this.qpRecordsSubj$.next(users));
    this.subscriptions.add(sub);
  }
  async loadAllQPUsers(query: ISearchQuery): Promise<void> {
    const sub = this.userHttp.getAllQPUsers(query).subscribe(users => this.allQPRecordsSubj$.next(users));
    this.subscriptions.add(sub);
  }
  loadUsersSourceSites(userUniqueId: string, query: ISearchQuery): void {
    const sub = this.userHttp
      .userSourceSitesSearch(userUniqueId, query)
      .subscribe(userSourceSites => this.userSourceSitesSub$.next(userSourceSites));
    this.subscriptions.add(sub);
  }
  loadQpsSourceSites(userUniqueId: string, query: ISearchQuery): void {
    const sub = this.userHttp
      .qpSourceSitesSearch(userUniqueId, query)
      .subscribe(qpSourceSites => this.userSourceSitesSub$.next(qpSourceSites));
    this.subscriptions.add(sub);
  }

  loadUserFillSites(userUniqueId: string, query: ISearchQuery): void {
    const sub = this.userHttp.userFillSiteSearch(userUniqueId, query).subscribe(userFillSites => this.userFillSiteSub$.next(userFillSites));
    this.subscriptions.add(sub);
  }
  loadQpFillSites(userUniqueId: string, query: ISearchQuery): void {
    const sub = this.userHttp.qpFillSiteSearch(userUniqueId, query).subscribe(userFillSites => this.userFillSiteSub$.next(userFillSites));
    this.subscriptions.add(sub);
  }

  clearUsers(): void {
    this.userRecordsSubj$.next(null);
  }

  deactivateUser(userUniqueId: string, fn = () => { }, errFn = () => { }) {
    const sub = this.userHttp.deactivateUser(userUniqueId).subscribe(fn, errFn);
    this.subscriptions.add(sub);
  }

  approveUser(userUniqueId: string, fn = () => { }, errFn = () => { }) {
    const sub = this.userHttp.approveUser(userUniqueId).subscribe(fn, errFn);
    this.subscriptions.add(sub);
  }

  deleteUserForever(userUniqueId: string, fn = () => { }, errFn = () => { }) {
    const sub = this.userHttp.deleteUserForever(userUniqueId).subscribe(fn, errFn);
    this.subscriptions.add(sub);
  }

  deleteUser(userUniqueId: string, fn = () => { }, errFn = () => { }) {
    const sub = this.userHttp.deleteUser(userUniqueId).subscribe(fn, errFn);
    this.subscriptions.add(sub);
  }

  loadUser(userUniqueId: string) {
    return this.userHttp
      .getUser(userUniqueId)
      .toPromise()
      .then(user => {
        this.userSubj$.next(user);
        return user;
      });
  }
  loadQP(userUniqueId: string) {
    return this.userHttp
      .getQualifiedPerson(userUniqueId)
      .toPromise()
      .then(user => {
        this.userSubj$.next(user);
        return user;
      });
  }

  editUserRolesAndPermissions(payload: EditUserPoliciesAndRolesCommand, fn = () => { }) {
    const sub = this.userHttp.editUserRolesAndPermissions(payload).subscribe(fn);
    this.subscriptions.add(sub);
  }

  editUserProfile(userUniqueId: string, payload: EditUserProfileCommand, fn: () => void) {
    const sub = this.userHttp.editUserProfile(userUniqueId, payload).subscribe(fn);
    this.subscriptions.add(sub);
  }

  inviteNewUser(legalEntityUniqueId: string, payload: InviteUserCommand, fn = () => { }, errFn = () => { }) {
    const sub = this.userHttp.inviteUser(legalEntityUniqueId, payload).subscribe(fn, errFn);
    this.subscriptions.add(sub);
  }
  inviteQPNewUserById(legalEntityUniqueId: string, payload: InviteQualifiedPersonListCommand) {
    return this.userHttp.inviteQPUserById(legalEntityUniqueId, payload);
  }
  inviteQPUserWithAccess(legalEntityUniqueId: string, payload: InviteQualifiedPersonCommand, fn = () => { }) {
    const sub = this.userHttp.inviteQPUserWithAccess(legalEntityUniqueId, payload).subscribe(fn);
    this.subscriptions.add(sub);
  }
  removeQPUserAccess(legalEntityUniqueId: string, qpUniqueId: string, fn = () => { }) {
    const sub = this.userHttp.removeQPUserAccess(legalEntityUniqueId, qpUniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }
  inviteQPNewUser(payload: InviteLegalEntityCommand) {
    return this.userHttp.inviteQPUser(payload);
  }
}
